import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Truck, MapPin, Phone, NavigationArrow, Camera, PenNib, CheckCircle,
  Calendar, SignOut, Package, ListBullets, Eraser
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [includeDelivered, setIncludeDelivered] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [confirmingOrder, setConfirmingOrder] = useState(null); // order being confirmed
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const authHeader = () => {
    const t = localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const [authError, setAuthError] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [meR, listR, statsR] = await Promise.all([
        axios.get(`${API_URL}/api/driver/me`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/driver/deliveries`, {
          headers: authHeader(),
          params: { date: date || undefined, include_delivered: includeDelivered },
        }),
        axios.get(`${API_URL}/api/driver/deliveries/stats`, { headers: authHeader() }),
      ]);
      setMe(meR.data);
      setDeliveries(listR.data);
      setStats(statsR.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        setAuthError(true);
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [date, includeDelivered]);

  useEffect(() => {
    if (authError) {
      toast.error('Session expirée — reconnexion requise');
      navigate('/livreur/login');
    }
  }, [authError, navigate]);

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/livreur/login');
  };

  const startDelivery = async (id) => {
    try {
      await axios.post(`${API_URL}/api/driver/deliveries/${id}/start`, null, { headers: authHeader() });
      toast.success('Livraison démarrée');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const openConfirm = (order) => {
    setConfirmingOrder(order);
    setNotes('');
    setPhotoFile(null);
    setTimeout(() => clearSignature(), 50);
  };

  // ── Signature canvas helpers ──
  const getCtx = () => canvasRef.current?.getContext('2d');
  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const startDraw = (e) => { drawingRef.current = true; const p = getPos(e); const ctx = getCtx(); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = getCtx();
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const stopDraw = () => { drawingRef.current = false; };
  const clearSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const confirmDelivery = async () => {
    if (!confirmingOrder) return;
    setBusy(true);
    try {
      const fd = new FormData();
      const c = canvasRef.current;
      // Detect if canvas has any drawing (compare to blank)
      if (c) {
        const dataUrl = c.toDataURL('image/png');
        // crude check: blank png is ~ <2KB at 500x180
        if (dataUrl.length > 4000) fd.append('signature_b64', dataUrl);
      }
      if (photoFile) fd.append('photo', photoFile);
      if (notes) fd.append('notes', notes);
      await axios.post(`${API_URL}/api/driver/deliveries/${confirmingOrder.id}/confirm`,
        fd, { headers: { ...authHeader() } });
      toast.success('Livraison confirmée');
      setConfirmingOrder(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const groupedByCity = deliveries.reduce((acc, d) => {
    const key = `${d.postal_code || '—'} · ${d.city || '—'}`;
    (acc[key] = acc[key] || []).push(d);
    return acc;
  }, {});

  const statusBadge = (s) => {
    if (s === 'shipping') return <Badge className="bg-amber-500">En cours</Badge>;
    if (s === 'preparing') return <Badge className="bg-slate-500">À préparer</Badge>;
    if (s === 'confirmed') return <Badge className="bg-blue-500">Confirmée</Badge>;
    if (s === 'delivered') return <Badge className="bg-emerald-600">Livrée</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b sticky top-0 z-30 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck size={28} className="text-[#0052FF]" />
            <div>
              <h1 className="font-bold text-lg">Tournée du livreur</h1>
              <p className="text-xs text-slate-500">{me?.name} · {me?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="driver-logout">
            <SignOut size={16} className="mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4 pb-24">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-2">
            <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.to_prepare}</p>
              <p className="text-[11px] text-blue-700 uppercase">À préparer</p>
            </CardContent></Card>
            <Card className="bg-amber-50 border-amber-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{stats.in_transit}</p>
              <p className="text-[11px] text-amber-700 uppercase">En cours</p>
            </CardContent></Card>
            <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">{stats.delivered_total}</p>
              <p className="text-[11px] text-emerald-700 uppercase">Livrées</p>
            </CardContent></Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-slate-500" />
              <Input
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-auto" data-testid="driver-date-filter"
              />
              <Button variant="outline" size="sm" onClick={() => setDate('')} data-testid="driver-clear-date">
                Toutes dates
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={includeDelivered}
                onChange={(e) => setIncludeDelivered(e.target.checked)}
                data-testid="driver-toggle-delivered" />
              Voir aussi les livrées
            </label>
          </CardContent>
        </Card>

        {/* Deliveries grouped by city */}
        {loading ? (
          <p className="text-center text-slate-500 py-8">Chargement…</p>
        ) : deliveries.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Package size={48} className="mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Aucune livraison pour cette date.</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedByCity).map(([city, list]) => (
            <div key={city}>
              <div className="flex items-center gap-2 px-1 py-2 sticky top-[58px] bg-slate-100 z-10">
                <MapPin size={18} className="text-[#0052FF]" />
                <h2 className="font-semibold text-slate-700">{city}</h2>
                <Badge variant="outline">{list.length}</Badge>
              </div>
              <div className="space-y-3">
                {list.map((o) => (
                  <Card key={o.id} className="overflow-hidden" data-testid={`delivery-card-${o.id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900">{o.customer_name}</p>
                          <p className="text-sm text-slate-600">{o.order_number}</p>
                        </div>
                        {statusBadge(o.status)}
                      </div>
                      <div className="text-sm text-slate-700">
                        <p className="flex items-start gap-1.5">
                          <MapPin size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
                          {o.address}, {o.postal_code} {o.city}
                        </p>
                        {o.notes && (
                          <p className="text-xs italic text-amber-700 bg-amber-50 px-2 py-1 rounded mt-1">
                            📝 {o.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                        <ListBullets size={14} /> {o.items_count} article(s) · {o.total?.toFixed(2)} €
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        {o.customer_phone && (
                          <Button asChild variant="outline" size="sm" data-testid={`call-${o.id}`}>
                            <a href={`tel:${o.customer_phone}`}>
                              <Phone size={14} className="mr-1" /> Appeler
                            </a>
                          </Button>
                        )}
                        <Button asChild variant="outline" size="sm" data-testid={`gmaps-${o.id}`}>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${o.address}, ${o.postal_code} ${o.city}`)}`}
                            target="_blank" rel="noopener noreferrer"
                          >
                            <NavigationArrow size={14} className="mr-1" /> Itinéraire
                          </a>
                        </Button>
                        {(o.status === 'confirmed' || o.status === 'preparing') && (
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600"
                                  onClick={() => startDelivery(o.id)} data-testid={`start-${o.id}`}>
                            <Truck size={14} className="mr-1" /> Démarrer livraison
                          </Button>
                        )}
                        {o.status !== 'delivered' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => openConfirm(o)} data-testid={`confirm-${o.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Livrée
                          </Button>
                        )}
                        {o.status === 'delivered' && o.delivery_proof?.signature_url && (
                          <a href={`${API_URL}${o.delivery_proof.signature_url}`} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-emerald-700 underline inline-flex items-center gap-1">
                            <PenNib size={12} /> Voir signature
                          </a>
                        )}
                        {o.status === 'delivered' && o.delivery_proof?.photo_url && (
                          <a href={`${API_URL}${o.delivery_proof.photo_url}`} target="_blank" rel="noopener noreferrer"
                             className="text-xs text-emerald-700 underline inline-flex items-center gap-1">
                            <Camera size={12} /> Voir photo
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </main>

      {/* Confirm Delivery Dialog */}
      <Dialog open={!!confirmingOrder} onOpenChange={(o) => !o && setConfirmingOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmer la livraison</DialogTitle>
            <DialogDescription>{confirmingOrder?.customer_name} — {confirmingOrder?.order_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm flex items-center gap-1"><PenNib size={14} /> Signature client</Label>
              <div className="border-2 border-dashed border-slate-300 rounded mt-1 relative bg-white">
                <canvas
                  ref={canvasRef}
                  width={500} height={180}
                  className="w-full h-[150px] touch-none rounded"
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                  data-testid="signature-canvas"
                />
                <Button type="button" size="sm" variant="outline" className="absolute top-1 right-1 h-7"
                        onClick={clearSignature} data-testid="clear-signature">
                  <Eraser size={12} className="mr-1" /> Effacer
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm flex items-center gap-1"><Camera size={14} /> Photo (preuve)</Label>
              <Input
                type="file" accept="image/*" capture="environment"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                data-testid="photo-input"
              />
            </div>

            <div>
              <Label className="text-sm">Notes (optionnel)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Déposé chez le voisin, etc."
                        data-testid="confirm-notes" />
            </div>

            <Button onClick={confirmDelivery} disabled={busy}
                    className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-submit">
              {busy ? 'Envoi…' : 'Valider la livraison'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
